import React, { useState } from 'react';
import { Button, Flex, Typography, theme } from 'antd';
import { CheckOutlined, CopyOutlined } from '@ant-design/icons';

const { Text } = Typography;

/** Copies text, and says whether the browser let us. */
export const copyToClipboard = async (value: string): Promise<boolean> => {
  try {
    await navigator.clipboard.writeText(value);

    return true;
  } catch {
    // No Clipboard API — an old browser, or an insecure context.
    return false;
  }
};

export type CopyableValueProps = {
  value: string;
  variant?: 'box' | 'inline';
};

/**
 * A value the customer has to carry into somebody else's user interface: a
 * Cloudflare nameserver, or the zone name they must pick from a Cloudflare list.
 *
 * The value is always selectable text, and the control says when the browser
 * refused the clipboard, so the customer is never left with a button that
 * silently does nothing (a PRE-3291 failure condition).
 */
const CopyableValue = ({ value, variant = 'box' }: CopyableValueProps) => {
  const { token } = theme.useToken();
  const [state, setState] = useState<'idle' | 'copied' | 'refused'>('idle');

  const onCopy = async () => {
    setState((await copyToClipboard(value)) ? 'copied' : 'refused');
  };

  const iconStyle = variant === 'inline' ? { color: token.colorPrimary } : undefined;

  const copyControl = (
    <Button
      size="small"
      type={variant === 'inline' ? 'text' : undefined}
      icon={state === 'copied' ? <CheckOutlined style={iconStyle} /> : <CopyOutlined style={iconStyle} />}
      aria-label={`Copy ${value}`}
      onClick={onCopy}
    >
      {variant === 'box' && (state === 'copied' ? 'Copied' : 'Copy')}
    </Button>
  );

  return (
    <Flex vertical gap={token.marginXXS}>
      {variant === 'inline' ? (
        <Flex align="center" gap={token.marginXXS}>
          {copyControl}
          <Text strong style={{ userSelect: 'all' }}>
            {value}
          </Text>
        </Flex>
      ) : (
        <Flex justify="space-between" align="center" gap={token.marginSM}>
          <Text code copyable={false} style={{ userSelect: 'all' }}>
            {value}
          </Text>
          {copyControl}
        </Flex>
      )}
      {state === 'refused' && (
        <Text type="secondary">Your browser would not let us copy this. Select and copy it by hand.</Text>
      )}
    </Flex>
  );
};

export default CopyableValue;
